/**
 * RingWorkspace — the LIST RAIL + THREAD PANE of the Ring Center, rebuilt
 * to match the approved design preview (gold sub-tab rail, colour-accented
 * rows, navy/white message bubbles, big composer) but wired to LIVE data:
 *   • inbox rollup  → callsAPI.getRecent + voicemailAPI.getAll + smsAPI.getThreads
 *   • conversation  → smsAPI.getConversation
 *   • send          → smsAPI.send
 *   • call          → shared useCall() bus
 */
import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import {
  Phone, MessageSquare, Voicemail as VMIcon, PhoneMissed, PhoneIncoming,
  PanelRightClose, PanelRightOpen, Paperclip, Send,
  Trash2, X, CheckSquare, Square, ListChecks, Archive, RotateCcw, Search, ChevronDown, Plus, UserPlus, Pencil,
} from "lucide-react";
import { callsAPI, voicemailAPI, smsAPI, contactsAPI, twilioAPI, adminAPI } from "../../services/api";
import { useInboxRollup, last10 } from "../comms/useInboxRollup";
import EmptyState from "../../components/EmptyState";
import QuickAddContactModal from "../../components/comms/QuickAddContactModal";
import { useCall } from "../../components/layout/ActiveCallOverlay";
import { useAuth } from "../../context/AuthContext";
import { formatPhoneNumber } from "../../lib/utils";
import MmsMedia from "../../components/comms/MmsMedia";
import EmojiQuickPicker from "../../components/comms/EmojiQuickPicker";
import QuickReplyPicker from "../../components/comms/QuickReplyPicker";
import PasswordPromptModal from "../../components/comms/PasswordPromptModal";
import { dayLabel, DeliveryTicks } from "../../components/comms/smsDelivery";
import { toast } from "sonner";

const NAVY = "#032866";
const NAVY_DEEP = "#002244";
const GOLD = "#FFDA00";
const MUTED = "#7B8FA3";
const HAIRLINE = "rgba(3,40,102,0.08)";
const ROYAL = "#2A7CC4";
const RED = "#C0392B";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const FONT_HEAD = "'Manrope', sans-serif";
const FONT_BODY = "'IBM Plex Sans', sans-serif";

const SUBNAV = ["All", "Calls", "Voicemail", "Unassigned"];

// Company filter — custom button+popover (NOT a native <select>) so the
// dynamic option text lives in valid <button> nodes. Avoids the dev-only
// hydration warning caused by the editor wrapping <option> text in spans.
function CompanyFilter({ companies, activeCompanyId, onPickCompany }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const current = companies.find((c) => c.id === activeCompanyId);
  const label = current ? current.name : "All companies";
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen((v) => !v);
  };
  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        data-testid="rc-company-filter"
        onClick={toggle}
        className="flex items-center gap-1 text-[12px] font-semibold rounded-md px-2 py-1 hover:bg-slate-50 transition-colors"
        style={{ background: "#fff", color: NAVY_DEEP, border: `1px solid ${HAIRLINE}`, maxWidth: 170 }}
        title="Filter conversations by company"
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={13} className="shrink-0" />
      </button>
      {open && pos && (
        <>
          <button className="fixed inset-0 z-[60] cursor-default" onClick={() => setOpen(false)} aria-label="Close company filter" />
          <div
            className="fixed z-[61] w-52 max-h-72 overflow-y-auto rounded-md bg-white shadow-lg"
            style={{ top: pos.top, right: pos.right, border: `1px solid ${HAIRLINE}` }}
            data-testid="rc-company-filter-menu"
          >
            <button
              onClick={() => { onPickCompany(null); setOpen(false); }}
              data-testid="rc-company-opt-all"
              className="w-full text-left px-3 py-1.5 text-[12px] font-semibold hover:bg-slate-50 transition-colors"
              style={{ color: NAVY_DEEP, background: !activeCompanyId ? "rgba(3,40,102,0.06)" : "transparent" }}
            >
              All companies
            </button>
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => { onPickCompany(c.id); setOpen(false); }}
                data-testid={`rc-company-opt-${c.id}`}
                className="w-full text-left px-3 py-1.5 text-[12px] font-semibold hover:bg-slate-50 truncate transition-colors"
                style={{ color: NAVY_DEEP, background: activeCompanyId === c.id ? "rgba(3,40,102,0.06)" : "transparent" }}
              >
                {c.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function initials(name, phone) {
  const s = (name || "").trim();
  if (s && /[a-z]/i.test(s)) {
    const p = s.split(/\s+/);
    return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
  }
  return last10(phone).slice(-2) || "?";
}

function rowMeta(e) {
  // accent colour + icon + right-badge from the freshest channel
  if (e.latestKind === "call" && e.latestMissed) return { accent: RED, Icon: PhoneMissed };
  if (e.latestKind === "vm") return { accent: ROYAL, Icon: VMIcon };
  if (e.latestKind === "sms") return { accent: e.unreadSms ? AMBER : "transparent", Icon: MessageSquare };
  if (e.latestKind === "call") return { accent: e.latestDirection === "inbound" ? GREEN : "transparent", Icon: e.latestDirection === "inbound" ? PhoneIncoming : Phone };
  return { accent: "transparent", Icon: MessageSquare };
}

function timeLabel(ts) {
  if (!ts) return "";
  const d = new Date(ts), now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).replace(" ", "").toLowerCase();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yest";
  return d.toLocaleDateString([], { weekday: "short" });
}

export default function RingWorkspace({ sub, onSub, openKey, setOpenKey, compose, ctxOpen, setCtxOpen, onNewText, onNewCall }) {
  const { makeCall } = useCall();
  const { user } = useAuth();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // Iter 644 — SMS deletion is owner-only (+ super_admin break-glass).
  const canDelete = ["owner", "super_admin"].includes(user?.role);
  const [recentCalls, setRecentCalls] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [smsThreads, setSmsThreads] = useState([]);
  const [contactsIndex, setContactsIndex] = useState({});
  const [fromNumber, setFromNumber] = useState("");
  // Tenant isolation (restored iter 636): the inbox is scoped to ONE
  // company at a time so e.g. Avida & Bylyngo texts never intermingle.
  // `activeCompanyId === null` = "All companies" (opt-in); `undefined`
  // = not yet initialised (defaults to the first company on load).
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(undefined);
  // Conversation search — filters the list rail by contact name, phone
  // number, company, or message-preview content. (The top bar never
  // rendered the promised search box, and the list had no filter at all.)
  const [query, setQuery] = useState("");
  // "Search everywhere": the client filter only sees each thread's LATEST
  // preview line, so a match in an older message would be missed. We debounce
  // a backend search over full SMS history (GET /sms/search) and union the
  // matched phone numbers in, so those threads stay visible too.
  const [deepMatchKeys, setDeepMatchKeys] = useState(() => new Set());
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setDeepMatchKeys(new Set()); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await smsAPI.searchMessages(q);
        if (cancelled) return;
        const keys = new Set((r.data?.phones || []).map((p) => last10(p)).filter(Boolean));
        setDeepMatchKeys(keys);
      } catch { if (!cancelled) setDeepMatchKeys(new Set()); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const loadInbox = useCallback(async () => {
    const [rc, vm, sms] = await Promise.allSettled([
      callsAPI.getRecent(80), voicemailAPI.getAll({ limit: 80 }), smsAPI.getThreads(),
    ]);
    if (rc.status === "fulfilled") setRecentCalls(rc.value.data || []);
    if (vm.status === "fulfilled") setVoicemails(vm.value.data || []);
    if (sms.status === "fulfilled") setSmsThreads(sms.value.data || []);
  }, []);

  useEffect(() => {
    loadInbox();
    // EV-06: 30s is responsive enough for the inbox safety-net refresh (live
    // updates also arrive via websocket); pause entirely when the tab is hidden
    // and refresh immediately on refocus.
    const t = setInterval(() => { if (!document.hidden) loadInbox(); }, 30000);
    const onVis = () => { if (!document.hidden) loadInbox(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [loadInbox]);

  useEffect(() => {
    (async () => {
      try {
        const r = await contactsAPI.getAll();
        const idx = {};
        for (const c of (r.data || [])) { const k = last10(c.phone_number); if (k) idx[k] = c; }
        setContactsIndex(idx);
      } catch { /* empty */ }
      // Load companies (outbound lines) so each tenant's comms can be
      // viewed independently. Falls back to the caller's available
      // Twilio numbers when the admin companies list isn't reachable.
      try {
        const res = await adminAPI.getCompanies();
        const list = (res.data || []).filter(c => c.phone_number);
        list.sort((a, b) => (a.name?.toLowerCase().includes("avida") ? 0 : 1) - (b.name?.toLowerCase().includes("avida") ? 0 : 1));
        setCompanies(list);
        if (list.length) {
          setFromNumber(prev => prev || list[0].phone_number);
          setActiveCompanyId(prev => prev === undefined ? list[0].id : prev);
        } else {
          setActiveCompanyId(null);
        }
      } catch {
        try {
          const r = await twilioAPI.getAvailableNumbers();
          const mapped = (r.data || []).map(n => ({ id: n.company_id || n.phone_number, name: n.company_name || "My line", phone_number: n.phone_number || n.number }));
          setCompanies(mapped);
          if (mapped.length) {
            setFromNumber(prev => prev || mapped[0].phone_number);
            setActiveCompanyId(prev => prev === undefined ? mapped[0].id : prev);
          } else {
            setActiveCompanyId(null);
          }
        } catch { setActiveCompanyId(null); }
      }
    })();
  }, []);

  // Switching company also switches the outbound line so replies go
  // from the right tenant's number.
  const onPickCompany = useCallback((cid) => {
    setActiveCompanyId(cid);
    if (cid) {
      const c = companies.find(x => x.id === cid);
      if (c?.phone_number) setFromNumber(c.phone_number);
    }
  }, [companies]);

  const rollupCompanyId = activeCompanyId === undefined ? null : activeCompanyId;
  const { inboxItems } = useInboxRollup({ recentCalls, voicemails, smsThreads, contactsIndex, activeCompanyId: rollupCompanyId });

  const rows = useMemo(() => {
    let out = inboxItems.filter(e => !e.hidden);
    if (sub === "Calls") out = out.filter(e => e.calls.length > 0);
    else if (sub === "Texts") out = out.filter(e => !!e.smsThread);
    else if (sub === "Voicemail") out = out.filter(e => e.voicemails.length > 0);
    else if (sub === "Unassigned") out = out.filter(e => e.needsRename);
    const q = query.trim().toLowerCase();
    if (q) {
      const qDigits = q.replace(/\D/g, "");
      out = out.filter((e) => {
        const vmText = (e.voicemails || []).map((v) => v.transcription).filter(Boolean).join(" ");
        const hay = [e.displayName, e.contactName, e.companyName, e.latestSummary, vmText]
          .filter(Boolean).join(" ").toLowerCase();
        if (hay.includes(q)) return true;
        if (qDigits && last10(e.phone).includes(qDigits)) return true;
        // Full-history SMS match (older messages) resolved server-side.
        if (deepMatchKeys.has(last10(e.phone))) return true;
        return false;
      });
    }
    return out;
  }, [inboxItems, sub, query, deepMatchKeys]);

  // auto-select freshest row once so the shell opens as a full 3-pane
  const didAuto = useRef(false);
  useEffect(() => {
    if (!didAuto.current && !openKey && rows.length) { didAuto.current = true; setOpenKey(rows[0].key); }
  }, [rows, openKey, setOpenKey]);

  let openEntry = openKey ? (inboxItems.find(e => e.key === openKey)
    || inboxItems.find(e => (e.key || "").split("::")[0] === String(openKey).split("::")[0])) : null;

  // "New text" to someone not yet in the inbox — synthesize an ephemeral
  // thread so the composer opens for the fresh recipient.
  if (!openEntry && compose?.phone && openKey && last10(compose.phone) === last10(String(openKey))) {
    openEntry = {
      key: compose.phone,
      phone: compose.phone,
      displayName: compose.name || formatPhoneNumber(compose.phone),
      contactName: compose.name || "",
      ourNumber: fromNumber,
      calls: [], voicemails: [], smsThread: null,
      companyName: "", contactType: "",
    };
  }

  // ── Bulk multi-select for conversations (iter 637) ──
  const [threadSelectMode, setThreadSelectMode] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState(() => new Set());
  const toggleThread = useCallback((key) => setSelectedThreads(s => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  }), []);
  const exitThreadSelect = useCallback(() => { setThreadSelectMode(false); setSelectedThreads(new Set()); }, []);
  const [pwPrompt, setPwPrompt] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const requestBulkDeleteThreads = useCallback(() => {
    const targets = rows.filter(e => selectedThreads.has(e.key) && e.smsThread);
    if (!targets.length) return;
    setPwPrompt({
      title: `Delete ${targets.length} conversation(s)`,
      description: "Conversations are archived for 30 days — only the owner can restore them. Enter your account password to confirm.",
      confirmLabel: "Delete",
      onConfirm: async (pw) => {
        let ok = 0, failed = 0;
        for (const e of targets) {
          const our = e.smsThread?.our_number || e.ourNumber || "";
          if (!our) continue;
          try { await smsAPI.deleteThread(e.phone, our, pw); ok += 1; }
          catch (err) {
            if (err?.response?.status === 401) throw err; // bad password → keep modal open
            failed += 1;
          }
        }
        toast.success(`Archived ${ok} conversation(s)` + (failed ? `, ${failed} failed` : ""));
        if (openKey && targets.some(e => e.key === openKey)) setOpenKey(null);
        exitThreadSelect();
        setPwPrompt(null);
        loadInbox();
      },
    });
  }, [rows, selectedThreads, openKey, setOpenKey, exitThreadSelect, loadInbox]);

  return (
    <div className="flex-[2] min-w-0 flex overflow-hidden" style={{ fontFamily: FONT_BODY }}>
      {/* ── LIST RAIL ─────────────────────────────────────────── */}
      <div data-testid="rc-list-rail" className="w-[320px] shrink-0 flex flex-col bg-white" style={{ borderRight: `1px solid ${HAIRLINE}` }}>
        <div className="flex items-center gap-1 px-3 h-11 shrink-0 overflow-x-auto bg-white" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
          {SUBNAV.map((t) => (
            <span key={t} onClick={() => onSub?.(t)} className="text-[12px] font-bold px-2.5 py-1 rounded-md shrink-0 cursor-pointer transition-colors" style={t === sub ? { background: NAVY_DEEP, color: "#fff" } : { color: MUTED }} data-testid={`rc-sub-${t.toLowerCase()}`}>{t}</span>
          ))}
          <div className="ml-auto flex items-center gap-1 shrink-0">
            {companies.length > 1 && (
              <CompanyFilter companies={companies} activeCompanyId={activeCompanyId} onPickCompany={onPickCompany} />
            )}
            {canDelete && (
              <button
                data-testid="rc-archived-open"
                onClick={() => setArchiveOpen(true)}
                title="View archived (deleted) conversations"
                className="shrink-0 p-1.5 rounded-md hover:bg-slate-100 transition-colors"
                style={{ color: MUTED }}
              >
                <Archive size={16} strokeWidth={2} />
              </button>
            )}
            {canDelete && (
              <button
                data-testid="rc-thread-select-toggle"
                onClick={() => (threadSelectMode ? exitThreadSelect() : setThreadSelectMode(true))}
                title="Select conversations to delete"
                className="shrink-0 p-1.5 rounded-md hover:bg-slate-100 transition-colors"
                style={threadSelectMode ? { background: NAVY_DEEP, color: "#fff" } : { color: MUTED }}
              >
                <ListChecks size={16} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
        {/* Search bar — look up conversations by contact name, number, or content */}
        <div className="px-3 py-2 shrink-0 flex items-center gap-2" style={{ borderBottom: `1px solid ${HAIRLINE}`, background: "#fff" }}>
          <div className="flex items-center gap-2 rounded-lg px-2.5 h-9 flex-1 min-w-0" style={{ background: "rgba(3,40,102,0.05)", border: `1px solid ${HAIRLINE}` }}>
            <Search size={15} style={{ color: MUTED }} />
            <input
              data-testid="rc-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, number, or message…"
              className="flex-1 min-w-0 bg-transparent text-[13px] focus:outline-none"
              style={{ color: NAVY_DEEP }}
            />
            {query && (
              <button data-testid="rc-search-clear" onClick={() => setQuery("")} title="Clear search" style={{ color: MUTED }}>
                <X size={14} />
              </button>
            )}
          </div>
          {/* Quick-add: start a brand-new text or call to any number */}
          <div className="relative shrink-0">
            <button
              data-testid="rc-quick-add"
              onClick={() => setQuickAddOpen((v) => !v)}
              title="Start a new text or call"
              className="flex items-center gap-1 rounded-lg h-9 px-3 text-[12.5px] font-bold hover:brightness-95 transition-all"
              style={{ background: GOLD, color: NAVY_DEEP }}
            >
              <Plus size={16} strokeWidth={2.5} /> New
            </button>
            {quickAddOpen && (
              <>
                <div className="fixed inset-0 z-[70]" onClick={() => setQuickAddOpen(false)} data-testid="rc-quick-add-backdrop" />
                <div className="absolute right-0 mt-1.5 w-44 rounded-xl bg-white shadow-xl z-[71] overflow-hidden py-1" style={{ border: `1px solid ${HAIRLINE}` }} data-testid="rc-quick-add-menu">
                  <button
                    data-testid="rc-quick-add-text"
                    onClick={() => { setQuickAddOpen(false); onNewText?.(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-semibold hover:bg-slate-50 transition-colors"
                    style={{ color: NAVY_DEEP }}
                  >
                    <MessageSquare size={15} style={{ color: NAVY }} /> New text
                  </button>
                  <button
                    data-testid="rc-quick-add-call"
                    onClick={() => { setQuickAddOpen(false); onNewCall?.(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-semibold hover:bg-slate-50 transition-colors"
                    style={{ color: NAVY_DEEP }}
                  >
                    <Phone size={15} style={{ color: GREEN }} /> New call
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {threadSelectMode && (
          <div className="flex items-center justify-between px-3 py-1.5 shrink-0" style={{ background: "rgba(3,40,102,0.06)", borderBottom: `1px solid ${HAIRLINE}` }} data-testid="rc-thread-select-bar">
            <span className="text-[12px] font-semibold" style={{ color: NAVY_DEEP }}>{selectedThreads.size} selected</span>
            <div className="flex items-center gap-2">
              <button onClick={exitThreadSelect} className="text-[12px] font-semibold px-2 py-1" style={{ color: MUTED }} data-testid="rc-thread-select-cancel">Cancel</button>
              <button onClick={requestBulkDeleteThreads} disabled={selectedThreads.size === 0} className="flex items-center gap-1 text-[12px] font-bold px-2.5 py-1 rounded-md disabled:opacity-40" style={{ background: RED, color: "#fff" }} data-testid="rc-thread-select-delete"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        )}
        <div className="overflow-y-auto flex-1" data-testid="rc-list-body">
          {rows.length === 0 && (
            query.trim() ? (
              <div className="p-6 text-center text-[12.5px]" style={{ color: MUTED }} data-testid="rc-list-empty">
                No matches for “{query.trim()}”.
              </div>
            ) : (
              <EmptyState
                icon={MessageSquare}
                title="No conversations yet"
                hint="Calls, texts, and voicemails will show up here."
                testId="rc-list-empty"
              />
            )
          )}
          {rows.map((e) => {
            const { accent, Icon } = rowMeta(e);
            const selected = openEntry && e.key === openEntry.key;
            const checked = selectedThreads.has(e.key);
            const badge = e.unreadSms ? String(e.unreadSms) : (e.unheardVm ? "VM" : (e.needsCallback ? "!" : ""));
            const unread = !!(e.unreadSms || e.unheardVm || e.needsCallback);
            return (
              <div
                key={e.key}
                data-testid={`rc-row-${last10(e.phone)}`}
                onClick={() => (threadSelectMode ? toggleThread(e.key) : setOpenKey(e.key))}
                className="flex items-start gap-3 px-3 py-3 cursor-pointer"
                style={{ minHeight: 56, borderBottom: `1px solid ${HAIRLINE}`, borderLeft: `4px solid ${accent}`, background: (checked || selected) ? "rgba(3,40,102,0.06)" : "transparent" }}
              >
                {threadSelectMode && (
                  <div className="mt-0.5 shrink-0" style={{ color: checked ? NAVY : MUTED }} data-testid={`rc-row-check-${last10(e.phone)}`}>
                    {checked ? <CheckSquare size={16} /> : <Square size={16} />}
                  </div>
                )}
                <div className="mt-0.5" style={{ color: accent === "transparent" ? MUTED : accent }}><Icon size={16} strokeWidth={1.9} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {unread ? <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent === "transparent" ? NAVY : accent }} /> : null}
                      <span className="text-[13px] truncate" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD, fontWeight: unread ? 800 : 600 }}>{e.displayName}</span>
                    </span>
                    <span className="text-[11px] shrink-0" style={{ color: unread ? NAVY_DEEP : MUTED, fontWeight: unread ? 700 : 400 }}>{timeLabel(e.latestTs)}</span>
                  </div>
                  <div className="text-[12px] leading-snug line-clamp-2" style={{ color: e.unreadSms || e.needsCallback || e.unheardVm ? NAVY_DEEP : MUTED }}>{e.latestSummary || formatPhoneNumber(e.phone)}</div>
                  {!activeCompanyId && e.companyName ? (
                    <div className="mt-1 inline-flex items-center text-[10px] font-bold rounded px-1.5 py-0.5" style={{ background: "rgba(3,40,102,0.06)", color: NAVY }} data-testid={`rc-row-company-${last10(e.phone)}`}>{e.companyName}</div>
                  ) : null}
                </div>
                {badge ? <span className="shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 mt-0.5" style={{ background: accent === "transparent" ? MUTED : accent, color: "#fff" }}>{badge}</span> : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── THREAD PANE ───────────────────────────────────────── */}
      {openEntry ? (
        <ThreadPane
          entry={openEntry}
          fromNumber={fromNumber}
          canDelete={canDelete}
          ctxOpen={ctxOpen}
          setCtxOpen={setCtxOpen}
          onCall={() => makeCall(openEntry.phone, fromNumber || openEntry.ourNumber)}
          onSent={loadInbox}
          onThreadDeleted={() => { setOpenKey(null); loadInbox(); }}
        />
      ) : (
        <div className="flex-1 min-w-0 flex items-center justify-center" style={{ background: "#F8FAFC" }}>
          <div className="text-center px-6">
            <MessageSquare className="w-9 h-9 mx-auto mb-3" style={{ color: "#94A3B8" }} />
            <div className="text-[15px] font-semibold" style={{ color: NAVY, fontFamily: FONT_HEAD }}>Select a conversation</div>
            <div className="text-[12.5px] mt-1" style={{ color: MUTED }}>Pick a thread from the list to view messages and reply.</div>
          </div>
        </div>
      )}

      <PasswordPromptModal
        open={!!pwPrompt}
        title={pwPrompt?.title}
        description={pwPrompt?.description}
        confirmLabel={pwPrompt?.confirmLabel}
        onConfirm={pwPrompt?.onConfirm}
        onClose={() => setPwPrompt(null)}
      />
      {archiveOpen && <ArchivedPanel onClose={() => setArchiveOpen(false)} onChanged={loadInbox} />}
    </div>
  );
}

function ThreadPane({ entry, fromNumber, canDelete, ctxOpen, setCtxOpen, onCall, onSent, onThreadDeleted }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msgSelectMode, setMsgSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState(() => new Set());
  const [pwPrompt, setPwPrompt] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const ourNumber = entry.smsThread?.our_number || entry.ourNumber || fromNumber || "";

  const loadConversation = useCallback(async () => {
    if (!entry?.phone) return;
    setLoading(true);
    try {
      const r = await smsAPI.getConversation(entry.phone, entry.ourNumber || null, 100);
      const list = r.data?.messages || r.data || [];
      setMessages(Array.isArray(list) ? list : []);
    } catch { setMessages([]); }
    finally { setLoading(false); }
  }, [entry?.phone, entry?.ourNumber]);

  useEffect(() => { setMessages([]); setDraft(""); setAttachment(null); setMsgSelectMode(false); setSelectedMsgIds(new Set()); loadConversation(); }, [loadConversation]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages]);

  const vmTranscript = entry.voicemails?.find(v => v.transcription)?.transcription;

  const acceptFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Attachment must be under 5MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setAttachment(f);
  };

  const send = async () => {
    const body = draft.trim();
    if ((!body && !attachment) || sending) return;
    const from = ourNumber;
    if (!from) { toast.error("No outbound line for this conversation — pick a company first."); return; }
    setSending(true);
    const stashed = attachment;
    setDraft("");
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    try {
      let mediaUrls;
      if (stashed) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", stashed);
        const up = await smsAPI.uploadMedia(fd);
        mediaUrls = [up.data.url];
        setUploading(false);
      }
      await smsAPI.send({ to_number: entry.phone, from_number: from, body, message: body, media_urls: mediaUrls });
      if (mediaUrls) toast.success("MMS sent");
      await loadConversation();
      onSent?.();
    } catch (e) {
      setDraft(body);
      setAttachment(stashed);
      toast.error(e?.response?.data?.detail || "Couldn't send message");
    } finally { setSending(false); setUploading(false); }
  };

  const deleteMessage = (m) => {
    const id = m.id || m.message_id;
    if (!id) { toast.error("This message can't be deleted."); return; }
    setPwPrompt({
      title: "Delete this message",
      description: "The message is archived for 30 days — only the owner can restore it. Enter your account password to confirm.",
      confirmLabel: "Delete",
      onConfirm: async (pw) => {
        await smsAPI.deleteMessage(id, pw);
        setMessages(prev => prev.filter(x => (x.id || x.message_id) !== id));
        toast.success("Message archived");
        setPwPrompt(null);
        onSent?.();
      },
    });
  };

  const deleteThread = () => {
    if (!entry?.phone) return;
    if (!ourNumber) { toast.error("Can't delete — this thread has no outbound line on record."); return; }
    setPwPrompt({
      title: "Delete this conversation",
      description: "The whole conversation is archived for 30 days — only the owner can restore it. Enter your account password to confirm.",
      confirmLabel: "Delete",
      onConfirm: async (pw) => {
        await smsAPI.deleteThread(entry.phone, ourNumber, pw);
        toast.success("Conversation archived");
        setPwPrompt(null);
        onThreadDeleted?.();
      },
    });
  };

  const insertText = (t, isEmoji = false) => {
    setDraft(d => {
      if (!d) return String(t).slice(0, 6400);
      if (isEmoji) return (d + t).slice(0, 6400);
      return ((d.endsWith(" ") || d.endsWith("\n")) ? d + t : d + " " + t).slice(0, 6400);
    });
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const toggleMsg = (id) => setSelectedMsgIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitMsgSelect = () => { setMsgSelectMode(false); setSelectedMsgIds(new Set()); };
  const bulkDeleteMessages = () => {
    const ids = Array.from(selectedMsgIds);
    if (!ids.length) return;
    setPwPrompt({
      title: `Delete ${ids.length} message(s)`,
      description: "Messages are archived for 30 days — only the owner can restore them. Enter your account password to confirm.",
      confirmLabel: "Delete",
      onConfirm: async (pw) => {
        let ok = 0, failed = 0;
        for (const id of ids) {
          try { await smsAPI.deleteMessage(id, pw); ok += 1; }
          catch (err) {
            if (err?.response?.status === 401) throw err; // bad password → keep modal open
            failed += 1;
          }
        }
        setMessages(prev => prev.filter(m => !selectedMsgIds.has(m.id || m.message_id)));
        toast.success(`Archived ${ok} message(s)` + (failed ? `, ${failed} failed` : ""));
        exitMsgSelect();
        setPwPrompt(null);
        loadConversation();
        onSent?.();
      },
    });
  };

  return (
    <div data-testid="rc-thread" className="flex-1 min-w-0 flex flex-col bg-white" style={{ fontFamily: FONT_BODY }}>
      {/* header */}
      <div className="flex items-center gap-3 px-4 h-14 shrink-0" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
        <div className="w-9 h-9 rounded-full grid place-items-center text-white text-[12px] font-bold" style={{ background: ROYAL }}>{initials(entry.displayName, entry.phone)}</div>
        <div className="min-w-0">
          <div className="text-[14px] font-bold truncate" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }} data-testid="rc-thread-name">{entry.displayName}</div>
          <div className="text-[11px] truncate" style={{ color: MUTED }}>{formatPhoneNumber(entry.phone)}{entry.contactType ? ` · ${entry.contactType}` : ""}{entry.companyName ? ` · ${entry.companyName}` : ""}</div>
        </div>
        <div className="ml-auto flex items-center gap-1.5" style={{ color: NAVY }}>
          {entry.needsRename ? (
            <button
              data-testid="rc-thread-add-contact"
              onClick={() => setQuickAddOpen(true)}
              title={entry.contact ? "Name this contact" : "Add to contacts"}
              className="flex items-center gap-1 h-8 px-2.5 rounded-full text-[12px] font-bold transition-colors"
              style={{ background: "rgba(3,40,102,0.06)", color: NAVY }}
            >
              <UserPlus size={15} strokeWidth={2} /> Add
            </button>
          ) : entry.contact ? (
            <button
              data-testid="rc-thread-edit-contact"
              onClick={() => setQuickAddOpen(true)}
              title="Edit this contact"
              className="flex items-center gap-1 h-8 px-2.5 rounded-full text-[12px] font-bold transition-colors hover:bg-slate-100"
              style={{ color: NAVY }}
            >
              <Pencil size={14} strokeWidth={2} /> Edit
            </button>
          ) : null}
          <button data-testid="rc-thread-call" onClick={onCall} title="Call" className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-[rgba(29,158,117,0.12)]" style={{ color: GREEN }}><Phone size={16} strokeWidth={2} /></button>
          <button data-testid="rc-thread-text" onClick={() => textareaRef.current?.focus()} title="Write a text" className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-slate-100"><MessageSquare size={16} strokeWidth={1.9} /></button>
          <button data-testid="rc-ctx-toggle" onClick={() => setCtxOpen(!ctxOpen)} title="Caller details" className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-[rgba(255,218,0,0.18)]">{ctxOpen ? <PanelRightClose size={17} strokeWidth={1.9} /> : <PanelRightOpen size={17} strokeWidth={1.9} />}</button>
          {canDelete && (
            <button data-testid="rc-msg-select-toggle" onClick={() => (msgSelectMode ? exitMsgSelect() : setMsgSelectMode(true))} title="Select messages to delete" className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-slate-100" style={{ color: msgSelectMode ? NAVY_DEEP : NAVY }}><ListChecks size={16} strokeWidth={1.9} /></button>
          )}
          {canDelete && (
            <button data-testid="rc-thread-delete" onClick={deleteThread} title="Delete conversation" className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-[rgba(192,57,43,0.1)]" style={{ color: RED }}><Trash2 size={16} strokeWidth={1.9} /></button>
          )}
        </div>
      </div>

      {msgSelectMode && (
        <div className="flex items-center justify-between px-4 py-1.5 shrink-0" style={{ background: "rgba(3,40,102,0.06)", borderBottom: `1px solid ${HAIRLINE}` }} data-testid="rc-msg-select-bar">
          <span className="text-[12px] font-semibold" style={{ color: NAVY_DEEP }}>{selectedMsgIds.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={exitMsgSelect} className="text-[12px] font-semibold px-2 py-1" style={{ color: MUTED }} data-testid="rc-msg-select-cancel">Cancel</button>
            <button onClick={bulkDeleteMessages} disabled={selectedMsgIds.size === 0} className="flex items-center gap-1 text-[12px] font-bold px-2.5 py-1 rounded-md disabled:opacity-40" style={{ background: RED, color: "#fff" }} data-testid="rc-msg-select-delete"><Trash2 size={13} /> Delete</button>
          </div>
        </div>
      )}

      {/* messages */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#F8FAFC" }} data-testid="rc-thread-body">
        {vmTranscript && (
          <div className="flex"><div className="max-w-[82%] rounded-2xl rounded-tl-sm px-3.5 py-2 text-[13px] bg-white" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY_DEEP }}>Voicemail transcript: “{vmTranscript}”</div></div>
        )}
        {loading && messages.length === 0 && (
          <div className="text-center text-[12px]" style={{ color: MUTED }}>Loading conversation…</div>
        )}
        {!loading && messages.length === 0 && !vmTranscript && (
          <div className="text-center text-[12px]" style={{ color: MUTED }}>No messages yet — start the conversation below.</div>
        )}
        {messages.map((m, i) => {
          const outbound = (m.direction || "").toLowerCase() === "outbound" || m.is_outbound || m.from_number === (entry.ourNumber || fromNumber);
          const text = m.body || m.message || m.message_body || "";
          const media = Array.isArray(m.media_urls) ? m.media_urls : [];
          const mediaKey = m.id || m.message_sid || m.sid || "";
          const delId = m.id || m.message_id || "";
          const checked = !!delId && selectedMsgIds.has(delId);
          const when = m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
          const prev = messages[i - 1];
          const showDay = !!dayLabel(m.created_at) && (!prev || dayLabel(prev.created_at) !== dayLabel(m.created_at));
          return (
            <Fragment key={m.id || i}>
              {showDay && (
                <div className="flex justify-center py-1.5" data-testid="rc-day-sep">
                  <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full" style={{ background: "rgba(3,40,102,0.06)", color: MUTED }}>{dayLabel(m.created_at)}</span>
                </div>
              )}
              <div
                className={`group flex items-center gap-1.5 ${msgSelectMode ? "cursor-pointer" : ""} ${outbound ? "justify-end" : "justify-start"}`}
                onClick={msgSelectMode && delId ? () => toggleMsg(delId) : undefined}
                data-testid={msgSelectMode ? `rc-msg-row-${mediaKey || i}` : undefined}
              >
                {msgSelectMode && (
                  <div className="shrink-0" style={{ color: checked ? NAVY : MUTED }} data-testid={`rc-msg-check-${mediaKey || i}`}>
                    {checked ? <CheckSquare size={15} /> : <Square size={15} />}
                  </div>
                )}
                {canDelete && !msgSelectMode && outbound && (
                  <button data-testid={`rc-msg-delete-${mediaKey || i}`} onClick={() => deleteMessage(m)} title="Delete message" className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" style={{ color: MUTED }}>
                    <Trash2 size={14} />
                  </button>
                )}
                <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-[13px] ${outbound ? "rounded-tr-sm text-white" : "rounded-tl-sm bg-white"}`} style={outbound ? { background: NAVY, outline: checked ? `2px solid ${GOLD}` : "none" } : { border: `1px solid ${HAIRLINE}`, color: NAVY_DEEP, outline: checked ? `2px solid ${NAVY}` : "none" }}>
                  {media.length > 0 && (
                    <div className="space-y-1.5 mb-1" data-testid="rc-msg-media">
                      {media.map((url, mi) => (
                        <MmsMedia key={`${mediaKey}-${mi}`} messageId={mediaKey} index={mi} rawUrl={url} outbound={outbound} />
                      ))}
                    </div>
                  )}
                  {text && <div className="whitespace-pre-wrap break-words">{text}</div>}
                  <div className={`flex items-center gap-1 mt-0.5 ${outbound ? "justify-end" : "justify-start"}`}>
                    {when && <span className="text-[10px]" style={{ color: outbound ? "rgba(255,255,255,0.6)" : MUTED }}>{when}</span>}
                    {outbound && <DeliveryTicks m={m} light />}
                  </div>
                </div>
                {canDelete && !msgSelectMode && !outbound && (
                  <button data-testid={`rc-msg-delete-${mediaKey || i}`} onClick={() => deleteMessage(m)} title="Delete message" className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" style={{ color: MUTED }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* composer */}
      <div className="shrink-0 px-4 pt-3 pb-4" style={{ borderTop: `1px solid ${HAIRLINE}`, background: "#fff" }}>
        {attachment && (
          <div className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg text-[12px]" style={{ background: "rgba(3,40,102,0.06)", color: NAVY_DEEP }} data-testid="rc-attachment-chip">
            <Paperclip size={13} />
            <span className="truncate max-w-[220px]">{attachment.name}</span>
            <button onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} title="Remove attachment" style={{ color: MUTED }}><X size={13} /></button>
          </div>
        )}
        <div className="flex flex-col rounded-xl" style={{ minHeight: 160, border: `1px solid ${HAIRLINE}` }}>
          <textarea
            ref={textareaRef}
            data-testid="rc-composer"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message…  (Enter to send, Shift+Enter for a new line)"
            className="flex-1 resize-none px-4 pt-3 text-[14px] leading-relaxed focus:outline-none"
            style={{ color: NAVY_DEEP, minHeight: 110 }}
          />
          <div className="flex items-center justify-between px-3 py-2" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
            <div className="flex items-center gap-3" style={{ color: MUTED }}>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                data-testid="rc-attach-input"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                onChange={(e) => acceptFile(e.target.files?.[0])}
              />
              <button data-testid="rc-attach-btn" onClick={() => fileInputRef.current?.click()} title="Attach a file (image / PDF / doc — sends as MMS)"><Paperclip size={17} /></button>
              <EmojiQuickPicker onPick={(c) => insertText(c, true)} testId="rc-emoji" />
              <QuickReplyPicker onPick={(t) => insertText(t)} testId="rc-quick-reply" />
              <button onClick={onCall} title="Call"><Phone size={17} /></button>
            </div>
            <button data-testid="rc-send" onClick={send} disabled={sending || uploading || (!draft.trim() && !attachment)} className="flex items-center gap-1.5 rounded-lg h-8 px-4 text-[13px] font-bold disabled:opacity-50" style={{ background: GOLD, color: NAVY_DEEP }}>
              <Send size={14} /> {uploading ? "Uploading…" : sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
        <div className="text-[11px] mt-1" style={{ color: MUTED }}>From: {ourNumber ? formatPhoneNumber(ourNumber) : "default line"}</div>
      </div>

      <PasswordPromptModal
        open={!!pwPrompt}
        title={pwPrompt?.title}
        description={pwPrompt?.description}
        confirmLabel={pwPrompt?.confirmLabel}
        onConfirm={pwPrompt?.onConfirm}
        onClose={() => setPwPrompt(null)}
      />

      <QuickAddContactModal
        open={quickAddOpen}
        phone={entry.phone}
        contactId={entry.contact?.id || null}
        editing={!!entry.contact && !entry.needsRename}
        defaultName={entry.contact ? `${entry.contact.first_name || ""} ${entry.contact.last_name || ""}`.trim() : ""}
        defaultType={entry.contact?.contact_type || entry.contactType || "family"}
        onClose={(saved) => { setQuickAddOpen(false); if (saved) onSent?.(); }}
      />
    </div>
  );
}


function ArchivedPanel({ onClose, onChanged }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwPrompt, setPwPrompt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await smsAPI.getArchived(); setThreads(r.data?.threads || []); }
    catch { setThreads([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (t) => {
    try {
      await smsAPI.restoreThread(t.phone_number, t.our_number);
      toast.success("Conversation restored");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't restore"); }
  };

  const requestPurge = (t) => {
    setPwPrompt({
      title: "Permanently delete",
      description: "This removes the conversation for good — it cannot be recovered afterwards. Enter your account password to confirm.",
      confirmLabel: "Delete forever",
      onConfirm: async (pw) => {
        await smsAPI.purgeThread(t.phone_number, t.our_number, pw);
        toast.success("Conversation permanently deleted");
        setPwPrompt(null);
        load(); onChanged?.();
      },
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" data-testid="rc-archived-panel">
      <div className="bg-white rounded-2xl shadow-2xl w-[560px] max-w-[94vw] max-h-[80vh] flex flex-col" style={{ fontFamily: FONT_BODY }}>
        <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
          <div className="flex items-center gap-2">
            <Archive size={17} style={{ color: NAVY_DEEP }} />
            <h3 className="text-[15px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>Archived conversations</h3>
          </div>
          <button onClick={onClose} data-testid="rc-archived-close" style={{ color: MUTED }}><X size={18} /></button>
        </div>
        <div className="px-5 pt-2 pb-1 text-[11.5px]" style={{ color: MUTED }}>Deleted conversations are kept here for 30 days. Only the owner can restore or permanently delete them.</div>
        <div className="overflow-y-auto flex-1 px-3 py-2">
          {loading && <div className="text-center text-[12.5px] py-8" style={{ color: MUTED }}>Loading…</div>}
          {!loading && threads.length === 0 && (
            <div className="text-center text-[12.5px] py-8" style={{ color: MUTED }} data-testid="rc-archived-empty">No archived conversations.</div>
          )}
          {threads.map((t) => (
            <div key={`${t.phone_number}-${t.our_number}`} data-testid={`rc-archived-row-${last10(t.phone_number)}`} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-slate-50" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold truncate" style={{ color: NAVY_DEEP }}>{t.contact_name || formatPhoneNumber(t.phone_number)}</div>
                <div className="text-[11.5px] truncate" style={{ color: MUTED }}>{formatPhoneNumber(t.phone_number)} · {t.count} message(s){t.company_name ? ` · ${t.company_name}` : ""}</div>
                <div className="text-[11px] truncate" style={{ color: MUTED }}>{t.last_message}</div>
              </div>
              <button data-testid={`rc-archived-restore-${last10(t.phone_number)}`} onClick={() => restore(t)} title="Restore" className="flex items-center gap-1 text-[12px] font-bold px-2.5 py-1 rounded-md shrink-0" style={{ background: "rgba(3,40,102,0.08)", color: NAVY_DEEP }}>
                <RotateCcw size={13} /> Restore
              </button>
              <button data-testid={`rc-archived-purge-${last10(t.phone_number)}`} onClick={() => requestPurge(t)} title="Delete permanently" className="shrink-0 p-1.5 rounded-md" style={{ color: RED }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
      <PasswordPromptModal
        open={!!pwPrompt}
        title={pwPrompt?.title}
        description={pwPrompt?.description}
        confirmLabel={pwPrompt?.confirmLabel}
        onConfirm={pwPrompt?.onConfirm}
        onClose={() => setPwPrompt(null)}
      />
    </div>
  );
}
